import React, { useContext, useEffect, useState } from 'react';
import { Grid, Typography, Box, Button, Card, CardContent, Chip, Dialog } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../Helpers/Contexts/authContext';
import { AuthUser } from '../../../interfaces/Contexts';
import { getFundList } from '../../../Utils/apiService';
import routes from '../../../Utils/routes';
import AddIcon from '@mui/icons-material/Add';
import CampaignIcon from '@mui/icons-material/Campaign';
import CampaignDialog from '../../Fundraise/campaignupload';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import moment from 'moment';

const FundraiseContent: React.FC = () => {
  const navigate = useNavigate();
  const {
    state: { user }
  } = useContext(AuthContext);
  const userData = user as AuthUser;
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    getFundList(userData?.applicantId)
      .then((res) => {
        // Filter campaigns created by the current user - using correct API response structure
        const matchingCampaigns = res?.data?.data?.filter((val: any) => val?.Fund_Created_By__c == userData.applicantId) || [];

        // Add status processing like in FundraiserII
        const processedCampaigns = matchingCampaigns.map((element: any) => {
          const isUpcoming = new Date(element?.Collection_Start_Date__c) > new Date();
          const isCompleted = new Date(element?.Collection_Last_Date__c) < new Date();

          if (element?.Fund_Category__r?.Name) {
            element['category'] = element?.Fund_Category__r?.Name;
          }

          if (!element?.Status__c) {
            element['status'] = 'In review';
          } else {
            if (element?.Status__c === 'Approved') {
              if (isUpcoming) {
                element['status'] = 'upcoming';
              } else if (element?.Close_Campaign__c || isCompleted) {
                element['status'] = 'completed';
              } else {
                element['status'] = 'onGoing';
              }
            } else {
              element['status'] = element?.Status__c;
            }
          }

          return element;
        });

        setCampaigns(processedCampaigns);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
        setCampaigns([]);
      });
  }, [userData?.applicantId]);

  const handleCreateCampaign = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleViewMore = () => {
    navigate(routes.fundraise);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'ongoing':
        return '#4CAF50';
      case 'upcoming':
        return '#2196F3';
      case 'completed':
        return '#9E9E9E';
      case 'rejected':
        return '#F44336';
      case 'in review':
        return '#FF9800';
      default:
        return '#FF9800';
    }
  };

  const renderCampaignCard = (campaign: any, index: number) => (
    <Card
      key={index}
      sx={{
        height: '100%',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: '#f9f9f9'
      }}
      onClick={() => navigate(`${routes.fundraiseDetails}/${campaign.Id}`)}
    >
      {/* Cover Image */}
      <Box
        sx={{
          height: '180px',
          width: '100%',
          position: 'relative',
          backgroundImage: `url(${campaign.Fund_Category__r?.Image_URL__c || 'https://via.placeholder.com/400x180'})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Chip
          label={campaign.status || 'Ongoing'}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            backgroundColor: getStatusColor(campaign.status),
            color: 'white',
            fontSize: '12px',
            height: '24px',
            borderRadius: '16px'
          }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, fontSize: '20px', fontWeight: 500, textAlign: "start" }}>
          {campaign.Name}
        </Typography>

        <Box sx={{ mb: 2, textAlign: "start" }}>
          <Chip
            label={campaign.Fund_Category__r?.Name || 'Infrastructure development'}
            variant="outlined"
            sx={{
              borderRadius: '20px',
              fontSize: '12px',
              color: '#555',
              borderColor: '#ddd'
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CalendarTodayIcon sx={{ fontSize: 18, color: '#666', mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {new Date(campaign.Collection_Start_Date__c).getDate()} - {new Date(campaign.Collection_Last_Date__c).getDate()} {new Date(campaign.Collection_Last_Date__c).toLocaleString('default', { month: 'long' })}, {new Date(campaign.Collection_Last_Date__c).getFullYear()}
          </Typography>
        </Box>

        {campaign.Target_Amount__c && (
          <Box sx={{ mt: 2, background: "#EDEEF1", borderRadius: "24px", padding: "5px 0px" }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Goal: ₹{Number(campaign.Target_Amount__c).toLocaleString('en-IN')}
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );

  return (
    <Box sx={{ p: 0 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography>Loading...</Typography>
        </Box>
      ) : campaigns.length > 0 ? (
        <>
          <Grid container spacing={2}>
            {campaigns.slice(0, 6).map((campaign: any, index: number) => (
              <Grid key={index} item xs={12} sm={6} md={4}>
                {renderCampaignCard(campaign, index)}
              </Grid>
            ))}
          </Grid>
          {campaigns.length > 6 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleViewMore}
                sx={{
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontSize: '12px',
                  fontWeight: 500
                }}
              >
                View More
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
          textAlign: 'center'
        }}>
          <CampaignIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
          <Typography variant="body1" sx={{ mb: 2, color: '#666' }}>
            You haven't created any campaigns yet
          </Typography>
       <Button
  variant="contained"
  startIcon={<AddIcon />}
  onClick={handleCreateCampaign}
  sx={{
    borderRadius: '6px',
    textTransform: 'none',
    fontSize: '12px',
    fontWeight: 500,
    backgroundColor: '#2C60AA', // ✅ default background
    color: '#fff',               // ✅ ensure text is white
    '&:hover': {
      backgroundColor: '#2C60AA', // ✅ same on hover
    },
  }}
>
  Create Your First Campaign
</Button>

        </Box>
      )}

      {/* Campaign Creation Dialog */}
      <CampaignDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onProceed={handleCloseDialog}
      />
    </Box>
  );
};

export default FundraiseContent;
